import { useState } from 'react';

/**
 * Custom hook for themed alerts
 * Replaces browser alert() and confirm()
 * 
 * Usage:
 * const { showAlert, showConfirm, AlertComponent } = useAlert();
 * 
 * showAlert('Success!', 'Operation completed', 'success');
 * showConfirm('Are you sure?', 'This cannot be undone', () => { ... });
 */
export const useAlert = () => {
  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    confirmText: 'OK',
    cancelText: 'Cancel',
    showCancel: false
  });

  const showAlert = (message, title = '', type = 'info', confirmText = 'OK') => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        title,
        message,
        type,
        confirmText,
        cancelText: 'Cancel',
        showCancel: false,
        onConfirm: () => resolve(true)
      });
    });
  };

  const showConfirm = (message, title = 'Confirm', confirmText = 'Confirm', cancelText = 'Cancel') => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        title,
        message,
        type: 'confirm',
        confirmText,
        cancelText,
        showCancel: true,
        onConfirm: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        }
      });
    });
  };

  const showSuccess = (message, title = 'Success') => {
    return showAlert(message, title, 'success');
  };

  const showError = (message, title = 'Error') => {
    return showAlert(message, title, 'error');
  };

  const showWarning = (message, title = 'Warning') => {
    return showAlert(message, title, 'warning');
  };

  const closeAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  return {
    showAlert,
    showConfirm,
    showSuccess,
    showError,
    showWarning,
    closeAlert,
    alertState,
    setAlertState
  };
};
